import { LTime } from '@app/components/date/LTime';
import { usePlaybackPlay } from '@app/core/playback/hooks/usePlayback';
import { PersonAppearance, PersonLane } from '@app/modules/persons/appearances/types';
import { TimeRange, toDateTimeStr } from '@mi-front/core';
import FaceOutlinedIcon from '@mui/icons-material/FaceOutlined';
import RecordVoiceOverOutlinedIcon from '@mui/icons-material/RecordVoiceOverOutlined';
import { Box, Stack, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { useSetCursor } from '../../../../stores/stream-screen.store';
import { usePersonsTrackCapture } from '../hooks/usePersonsTrackCapture';
import { TRACK_BORDER_PX, TRACK_HEIGHT_PX } from './lane-mini-timeline-layout';
import { LaneMiniTimelineBars } from './LaneMiniTimelineBars';
import { appearanceBarSx, MINI_TIMELINE_BAR_RADIUS_PX } from './persons-track-visual-tokens';

const TICK_COUNT = 5;
const LEGEND_ICON_SX = { fontSize: 12 };

/** Evenly spaced timestamps for the mini-timeline axis labels. */
function focusTicks(range: TimeRange<number>): number[] {
  const span = range.to - range.from;
  if (span <= 0) {
    return [range.from];
  }
  return Array.from({ length: TICK_COUNT }, (_, i) => range.from + (span * i) / (TICK_COUNT - 1));
}

type LaneMiniTimelineProps = {
  lane: PersonLane;
  range: TimeRange<number>;
  /** Appearance key currently hovered in the timeline or list. */
  hoveredKey: string | null;
  onHoverKey: (key: string | null) => void;
};

/**
 * Full-width interactive timeline in a Personas table detail panel.
 *
 * Single lane with face (solid) and voice (hatched) bars scaled to the focus
 * range. Hover syncs with the appearance list; click seeks and plays.
 */
export function LaneMiniTimeline({ lane, range, hoveredKey, onHoverKey }: LaneMiniTimelineProps) {
  const setCursor = useSetCursor();
  const play = usePlaybackPlay();
  const { appearanceBarClicked } = usePersonsTrackCapture();
  const ticks = focusTicks(range);

  const onPlay = (appearance: PersonAppearance) => {
    appearanceBarClicked(lane, appearance, 'results_table');
    setCursor(appearance.from);
    play({ startTs: appearance.from, playAfterSeek: true });
  };

  return (
    <Stack spacing={0.75} sx={{ width: '100%' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'medium' }}>
          <FormattedMessage
            id="logger.persons.table.timeline"
            defaultMessage="Apariciones en el foco"
          />
        </Typography>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Box
              sx={{
                width: 12,
                height: 5,
                borderRadius: `${MINI_TIMELINE_BAR_RADIUS_PX}px`,
                ...appearanceBarSx(lane.color, 'face'),
                cursor: 'default',
              }}
            />
            <FaceOutlinedIcon color="secondary" sx={LEGEND_ICON_SX} />
            <Typography variant="caption" color="text.secondary">
              <FormattedMessage id="logger.persons.source.face" defaultMessage="Rostro" />
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Box
              sx={{
                width: 12,
                height: 5,
                borderRadius: `${MINI_TIMELINE_BAR_RADIUS_PX}px`,
                ...appearanceBarSx(lane.color, 'voice'),
                cursor: 'default',
              }}
            />
            <RecordVoiceOverOutlinedIcon color="secondary" sx={LEGEND_ICON_SX} />
            <Typography variant="caption" color="text.secondary">
              <FormattedMessage id="logger.persons.source.voice" defaultMessage="Voz" />
            </Typography>
          </Stack>
        </Stack>
      </Stack>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: TRACK_HEIGHT_PX,
          bgcolor: 'action.hover',
          borderRadius: 1,
          border: `${TRACK_BORDER_PX}px solid`,
          borderColor: 'divider',
          overflow: 'hidden',
          boxSizing: 'border-box',
        }}
      >
        <LaneMiniTimelineBars
          items={lane.face}
          range={range}
          color={lane.color}
          source="face"
          hoveredKey={hoveredKey}
          onHoverKey={onHoverKey}
          onPlay={onPlay}
        />
        <LaneMiniTimelineBars
          items={lane.voice}
          range={range}
          color={lane.color}
          source="voice"
          hoveredKey={hoveredKey}
          onHoverKey={onHoverKey}
          onPlay={onPlay}
        />
      </Box>
      <Stack direction="row" justifyContent="space-between">
        {ticks.map((ts) => (
          <Typography key={ts} variant="caption" color="text.secondary">
            <LTime value={toDateTimeStr(ts)} hour12={false} />
          </Typography>
        ))}
      </Stack>
    </Stack>
  );
}
